import { isEmpty } from 'lodash';
import { InfiniteData, useMutation, useQueryClient } from 'react-query';
import { useRecoilValue } from 'recoil';

import { HttpMethod } from '@api/enums';
import { createHttpClient } from '@api/http-client';
import { END_POINTS } from '@constants/api-endpoints';
import { QUERY_KEYS } from '@constants/query-keys';
import { AlertType } from '@enums/alert';
import { showAlert } from '@lib/alert/alert-manager';
import { filterLabelIdsAtom } from '@store/atoms/filter-labelIds';

import { AssetEditData, AssetListResponse } from '../types';

export const useUpdateBrandAsset = () => {
  const client = createHttpClient();
  const queryClient = useQueryClient();
  const filterLabelIds = useRecoilValue(filterLabelIdsAtom);

  const mutation = useMutation(
    ({
      assetId,
      metadata,
      fileName,
      externalAssetMetadata,
      labels,
    }: AssetEditData) => {
      let labelIds;
      if (labels) {
        labelIds = Object.values(labels).map(label => label.id);
      }

      return client({
        method: HttpMethod.PATCH,
        url: END_POINTS.BRAND_LIBRARY.EDIT_ASSET(assetId),
        data: {
          ...(metadata && { metadata }),
          ...(fileName && { fileName: fileName }),
          ...(externalAssetMetadata && {
            externalAssetMetadata: externalAssetMetadata,
          }),
          ...(labelIds && { labelIds }),
        },
      });
    },
    {
      onSuccess: (_, variables) => {
        showAlert({
          type: AlertType.SUCCESS,
          message: variables.labels
            ? 'Label(s) applied successfully'
            : 'Asset updated successfully',
        });

        const cache = queryClient.getQueryData<InfiniteData<AssetListResponse>>(
          [QUERY_KEYS.BRAND_LIBRARY.BRAND_ASSETS_LIST, filterLabelIds],
        );

        if (isEmpty(cache?.pages)) {
          return;
        }

        const updatedPages = cache?.pages?.map(page => {
          const updatedItems = page.items.map(item => {
            if (item.id === variables.assetId) {
              return {
                ...item,
                ...(variables.metadata && {
                  metadata: variables.metadata,
                }),
                ...(variables.fileName && {
                  name: variables.fileName,
                }),
                ...(variables.externalAssetMetadata && {
                  externalAssetMetadata: variables.externalAssetMetadata,
                }),
                ...(variables.labels && {
                  labels: variables.labels,
                }),
              };
            }

            return item;
          });

          return { ...page, items: updatedItems };
        });

        queryClient.setQueryData<InfiniteData<AssetListResponse>>(
          [QUERY_KEYS.BRAND_LIBRARY.BRAND_ASSETS_LIST, filterLabelIds],
          {
            ...cache,
            pages: updatedPages || [],
            pageParams: cache?.pageParams || [],
          },
        );

        variables.onSuccess?.();
      },

      onError: () => {
        showAlert({
          type: AlertType.ERROR,
          message: 'Failed to update asset',
        });
      },
    },
  );

  return mutation;
};
